import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LinkedinIcon, LinkedinShareButton } from 'react-share';
import { useQueryParams } from 'hook/useQueryParams';

import { useGetCompanyBySlug } from '../api/getCompanyBySlug';
import { useTrackVisit } from 'hook/useTrackVisit';
import { DetailViewTabList } from 'components/TabList';
import { useAuth } from 'lib/auth';

import { SquareImage } from '../components/SquareImage';

import { BannerImage } from '../components';

import { Loader } from 'components/Loader';
import { DetailsTab } from '../components/DetailsTab';
import { AccordionButton, AccordionListItem } from 'components/Button';
import { paths } from 'routes';
import { DELEGON_URL } from 'configuration';
import { useLocale } from 'i18n/useLocale';
import { ContentLayout } from 'website/components/Layout/components/ContentLayout';
import { JobAdsTab } from '../components/JobAdsTab';
import { useRelatedJobAdsCountBySlug } from '../api/getRelatedJobAdsCountBySlug';
import { BlogsTab } from '../components/BlogsTab';
import { useRelatedBlogsCountBySlug } from '../api/getRelatedBlogsCountBySlug';

export const PublicProfile = ({ companyType = 'supplier', param = 'id' }) => {
  const { t } = useTranslation();
  const lang = useLocale();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const { account, user } = useAuth();  
  let { type, slug } = useParams();
  
  // The active tab lives in ?tab= so links like "See all" (?tab=blog) can switch it
  const tab = queryParams?.tab || 'details';
  const [ loading, setLoading ] = useState(false);
  const [timer, setTimer] = useState(null);

  const { data: company, isLoading: companyLoading, isFetched: isFetchedCompany } = useGetCompanyBySlug(type, slug);
  useTrackVisit('company_profile', company?.company_id, 'website');
  const {
    //@ts-ignore
    data: { count: relatedJobAdsCount },
    isLoading: relatedJobAdsCountLoading,
    isFetched: isFetchedJobAdsCount,
  } = useRelatedJobAdsCountBySlug(slug, {
      queryOptions: {
        enabled: !!slug
      },
  });

  const {
    //@ts-ignore
    data: { count: relatedBlogsCount },
    isLoading: relatedBlogsCountLoading,
    isFetched: isFetchedBlogsCount,
  } = useRelatedBlogsCountBySlug(slug, {
      queryOptions: {
        enabled: !!slug && type === 'buyer'
      },
  });

  const isLoading = companyLoading || relatedJobAdsCountLoading || (type === 'buyer' && relatedBlogsCountLoading);
  const isFetched = isFetchedCompany && isFetchedJobAdsCount && (type !== 'buyer' || isFetchedBlogsCount);
  

  const switchTab = (_, newTab) => navigate({ search: `?tab=${newTab}` }, { replace: true });

  const shareButton = (
    <Box sx={{ display: 'flex', '& svg': { display: 'block' } }}>
      <LinkedinShareButton url={`${DELEGON_URL}${paths.public.companyPublicProfile(type, slug, lang)}`}>
        <LinkedinIcon size={24} borderRadius={16} />
      </LinkedinShareButton>
    </Box>
  );

  useEffect(() => {  
    if (isLoading) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isFetched) {    
    if (loading) {      
      setTimeout(() => setLoading(false), 1000);
    }
  }

  return (
    // No hreflang: only the UI is translated, the profile content isn't (see CanonicalLocale in the backend's seo package)
    <ContentLayout hreflang={false}>
      {isLoading && !loading ? (
        <></>
      )
      : (
        loading ? (
          <Loader style={{marginTop: "40vh"}}/>
        )
        : (
          <Stack height="100%" width={1} sx={{bgcolor: "background.paper", pt: 4, px: {xs: 3, md: "auto"}, boxSizing: "border-box"}}>
            <BannerImage banner={company?.background_image_url} sx={{width: "100%"}} smallHeight={true} />

            <Stack direction="row">
              <SquareImage image={company?.square_logo_url || ''} bannerChangable={false} sx={{ flexShrink: 0, width: { xs: 75, md: 100 }, height: { xs: 75, md: 100 } }} />

              <Stack flex={1}>
                <Box
                  px={{ xs: 2, md: 4 }}
                  height={70}
                  sx={{
                    justifyContent: 'space-between',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Stack position="relative" flexDirection="column">
                    <Stack position="relative" flexDirection="row" gap={1}>
                      <Typography variant="h3" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{`${company?.Name}` || ''}</Typography>
                    </Stack>
                    <Typography variant='caption' fontWeight={600} sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>{`${company?.VAT}` || ''}</Typography>
                  </Stack>

                  {((account && account.super_admin) || (user && String(user.Company?.company_id) === String(company?.company_id))) ? (
                    <AccordionButton label={t('website:publicProfile.actions')} sx={{ width: 130 }} top={36} endAdornment={shareButton}>
                      <Stack gap={0.5}>
                        <AccordionListItem onClick={() => navigate(companyType === 'buyer' ? `${paths.buyersDetailsView(company?.company_id)}` : `${paths.suppliersDetailsView(company?.company_id)}`)}>{t('website:publicProfile.companyPage')}</AccordionListItem>
                      </Stack>
                    </AccordionButton>
                  ) : (
                    shareButton
                  )}
                </Box>
              </Stack>
            </Stack>

            <DetailViewTabList
              value={tab}
              onChange={switchTab}
              tabs={[
                { label: t('website:publicProfile.tabs.details'), value: 'details' },
                { label: t('website:publicProfile.tabs.jobAds'), value: 'job_ads', count: relatedJobAdsCount },
                ...(type === 'buyer' ? [{ label: t('website:publicProfile.tabs.blog'), value: 'blog', count: relatedBlogsCount, disabled: relatedBlogsCount === 0 }] : []),
              ]}
            />

            <Stack
              px={{xs: 0, md: 4}}
              py={3}
              bgcolor="background.paper"
              borderTop="1px solid"
              borderColor="border.main"
              gap={1}
              height="100%"
            >
              <DetailsTab currentTab={tab} companyProfile={company?.CompanyProfile} />
              <JobAdsTab id={company?.company_id} currentTab={tab} companyType={companyType} jobAdsCount={1} slug={slug} />
              {type === 'buyer' && <BlogsTab slug={slug} currentTab={tab} />}
            </Stack>
          </Stack>
        )
      )}
    </ContentLayout>
  );
};
